import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'

import type { TeamPlan } from './CreateTeamModal'
import type { DurationOption } from './TeamDurationModal'

interface TeamCheckoutModalProps {
  open: boolean
  onClose: () => void
  onBack: () => void
  onConfirm: () => void
  loading?: boolean
  teamData: {
    plan: TeamPlan | null
    duration: DurationOption | null
    name: string
    description: string
  }
}

export function TeamCheckoutModal({
  open,
  onClose,
  onBack,
  onConfirm,
  loading = false,
  teamData,
}: TeamCheckoutModalProps) {
  const { t } = useTranslation('team')
  const userStore = useUserStore()
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)
  const [isNeedCharge, setIsNeedCharge] = useState(false)

  // Calculate total price
  useEffect(() => {
    if (teamData.plan && teamData.duration) {
      // pricePerMonth is already in cents (from item.prices[0]?.priceCent)
      const price = teamData.plan.pricePerMonth * teamData.duration.months
      setTotalPrice(price)
    }
  }, [teamData.plan, teamData.duration])

  // Check if user needs to recharge
  useEffect(() => {
    const userBalance = userStore.account.totalBonus.moneyCent ?? 0
    setIsNeedCharge(userBalance < totalPrice)
  }, [totalPrice, userStore.account.totalBonus.moneyCent])

  const handleButtonClick = () => {
    if (isNeedCharge) {
      setRechargeModalOpen(true)
    } else {
      onConfirm()
    }
  }

  const getButtonLabel = () => {
    if (loading) return t('checkout.creating')
    if (isNeedCharge) return t('checkout.recharge')
    return t('checkout.createTeam')
  }

  if (!teamData.plan || !teamData.duration) {
    return null
  }

  return (
    <>
      <Modal open={open} onClose={onClose} className="w-[800px] h-[600px]">
        <Modal.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={onBack} className="p-3 hover:bg-panel-gray-200 rounded">
                <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
              </button>
              <span className="h6">
                <Trans ns="team" i18nKey="createTeam.step4Title">
                  Checkout and Create Team
                </Trans>
              </span>
              <span className="h5 text-tertiary">
                <Trans
                  ns="team"
                  i18nKey="createTeam.stepIndicator"
                  values={{ current: 4, total: 4 }}
                >
                  (4/4)
                </Trans>
              </span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-2">
            {/* Table Header */}
            <div className="bg-neutral-50 rounded-lg flex items-center">
              <div className="w-[200px] h-[44px]" />
              <div className="flex-1 px-4 py-3">
                <span className="t-sm text-secondary">{t('checkout.selected')}</span>
              </div>
              <div className="flex-1 px-4 py-3 text-right">
                <span className="t-sm text-secondary">{t('checkout.price')}</span>
              </div>
            </div>

            {/* Team Plan Row */}
            <div className="flex items-center border-b border-neutral-200">
              <div className="w-[200px] p-4">
                <span className="t-sm text-primary">{t('checkout.teamPlan')}</span>
              </div>
              <div className="flex-1 p-4">
                <span className="t-sm text-primary">{teamData.plan.name}</span>
              </div>
              <div className="flex-1 p-4 text-right">
                <span className="t-sm text-primary">
                  {teamData.plan.pricePerMonth === 0
                    ? t('checkout.freePerMonth')
                    : `${formatPrice(teamData.plan.pricePerMonth)} ${t('checkout.perMonth')}`}
                </span>
              </div>
            </div>

            {/* Team Duration Row */}
            <div className="flex items-center border-b border-neutral-200">
              <div className="w-[200px] p-4">
                <span className="t-sm text-primary">{t('checkout.teamDuration')}</span>
              </div>
              <div className="flex-1 p-4">
                <span className="t-sm text-primary">{teamData.duration.label}</span>
              </div>
              <div className="flex-1 p-4 text-right">
                <span className="t-sm text-primary">{formatPrice(totalPrice)}</span>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <span className="h4 text-highlight">{formatPrice(totalPrice)}</span>
              <span className="t-x-sb text-secondary rounded-full px-3 py-2 bg-neutral-50">
                {t('checkout.myBalance')}{' '}
                <span className="text-primary">
                  {formatPrice(userStore.account.totalBonus.moneyCent ?? 0)}
                </span>
              </span>
            </div>
            <div className="flex gap-4">
              <Button type="outline" onClick={onBack} disabled={loading}>
                {t('common.previous')}
              </Button>
              <Button type="secondary" onClick={handleButtonClick} disabled={loading}>
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {rechargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setRechargeModalOpen(false)
            userStore.getUserInfo()
          }}
          onClose={() => {
            setRechargeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
